import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthSession, AuthStatus } from '@emazy/shared-types';

import { authService as defaultAuthService } from '@/features/auth/services/authServiceFactory';
import type { AuthService } from '@/features/auth/services/AuthService';

import { AuthContext, type AuthContextValue } from './authContext';

type AuthProviderProps = PropsWithChildren<{ service?: AuthService }>;

export function AuthProvider({ children, service = defaultAuthService }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [session, setSession] = useState<AuthSession | null>(null);

  const refreshSession = useCallback(async () => {
    try {
      const restored = await service.getCurrentSession();
      setSession(restored);
      setStatus(restored ? 'authenticated' : 'unauthenticated');
    } catch (error) {
      if (import.meta.env.DEV) console.error('Session restoration failed safely.', error);
      setSession(null);
      setStatus('unauthenticated');
    }
  }, [service]);

  useEffect(() => {
    let active = true;
    service.getCurrentSession().then(
      (restored) => {
        if (!active) return;
        setSession(restored);
        setStatus(restored ? 'authenticated' : 'unauthenticated');
      },
      (error: unknown) => {
        if (!active) return;
        if (import.meta.env.DEV) console.error('Session restoration failed safely.', error);
        setSession(null);
        setStatus('unauthenticated');
      },
    );
    return () => {
      active = false;
    };
  }, [service]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      user: session?.user ?? null,
      isAuthenticated: status === 'authenticated' && session !== null,
      login: async (input) => {
        const authenticated = await service.login(input);
        setSession(authenticated);
        setStatus('authenticated');
        return authenticated;
      },
      register: (input) => service.register(input),
      verifyEmail: (input) => service.verifyEmail(input),
      resendVerificationCode: (input) => service.resendVerificationCode(input),
      forgotPassword: (input) => service.forgotPassword(input),
      resetPassword: (input) => service.resetPassword(input),
      logout: async () => {
        try {
          await service.logout();
        } finally {
          setSession(null);
          setStatus('unauthenticated');
        }
      },
      refreshSession,
    }),
    [refreshSession, service, session, status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
